import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { getCheckoutUrl } from '../utils/getCheckoutUrl';
import { trackAddToCart } from '../utils/metaPixel';
interface HeroSectionProps {
  addedBumps: Record<string, boolean>;
}
const TIMER_DURATION = 15 * 60; // 15 minutes in seconds
export function HeroSection({
  addedBumps
}: HeroSectionProps) {
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    const calculateTimeLeft = () => {
      const storedEndTime = localStorage.getItem('offerEndTime');
      const now = Math.floor(Date.now() / 1000);
      if (!storedEndTime || now > parseInt(storedEndTime, 10)) {
        const newEndTime = now + TIMER_DURATION;
        localStorage.setItem('offerEndTime', newEndTime.toString());
        return TIMER_DURATION;
      }
      return parseInt(storedEndTime, 10) - now;
    };
    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Auto-reset when it hits 0
          const now = Math.floor(Date.now() / 1000);
          localStorage.setItem('offerEndTime', (now + TIMER_DURATION).toString());
          return TIMER_DURATION;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const minutes = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const seconds = (timeLeft % 60).toString().padStart(2, '0');
  // Animation variants for staggered entrance
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section className="relative min-h-screen pt-28 pb-20 px-6 overflow-hidden flex flex-col items-center text-center" data-id="element-38">
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-accent/5 rounded-full blur-[120px] pointer-events-none -z-10" data-id="element-39" />

      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="w-full max-w-4xl mx-auto flex flex-col items-center" data-id="element-40">
        {/* 1. Título */}
        <motion.h1 variants={itemVariants} className="md:text-5xl lg:text-6xl font-serif text-text-primary mb-5 font-[900] text-[20px]" data-id="element-41">
          De no saber qué publicar… a crear contenido viral todos los días
          (aunque empieces desde cero)
        </motion.h1>

        {/* 2. Subtítulo */}
        <motion.p variants={itemVariants} className="md:text-xl text-text-secondary mb-10 max-w-2xl text-[16px]" data-id="element-42">
          Sin experiencia, sin editar y con ayuda de inteligencia artificial.
        </motion.p>

        {/* 3. Video */}
        <motion.div variants={itemVariants} className="w-full max-w-3xl mx-auto mb-14 rounded-xl overflow-hidden shadow-2xl shadow-black/60 border border-dark-tertiary bg-dark-secondary" data-id="element-43">
          <div className="relative w-full" style={{
          paddingBottom: '56.25%'
        }} data-id="element-44">
            <iframe src="https://www.youtube.com/embed/gVGM1LWzyYg?rel=0" title="Video de presentación" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-45" />
          </div>
        </motion.div>

        {/* 4. Texto dolor */}

        {/* 5. Texto promesa */}
        <motion.p variants={itemVariants} className="md:text-2xl font-semibold text-text-primary mb-12 max-w-3xl text-[16px]" data-id="element-46">
          En menos de 1 hora puedes tener tu primer video listo sin experiencia
          ni complicaciones.
        </motion.p>

        {/* 6. Temporizador */}
        <motion.div variants={itemVariants} className="mb-10 flex flex-col items-center w-full" data-id="element-47">
          <div className="relative w-full max-w-md" data-id="element-48">
            {/* Animated glow behind timer */}
            <motion.div animate={{
            opacity: [0.4, 0.8, 0.4]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute inset-0 bg-accent/10 rounded-2xl blur-xl -z-10" data-id="element-49" />

            <div className="bg-dark-secondary/80 backdrop-blur-sm border border-accent/30 rounded-2xl px-6 py-6 md:px-8 md:py-8" data-id="element-50">
              <p className="text-accent font-semibold mb-5 text-base md:text-lg tracking-wide uppercase text-center" data-id="element-51">
                ⏳ Precio de lanzamiento termina en
              </p>

              {mounted && <div className="flex items-center justify-center gap-4 md:gap-6 mb-5" data-id="element-52">
                  {/* Minutes */}
                  <div className="flex flex-col items-center" data-id="element-53">
                    <motion.div key={minutes} initial={{
                  scale: 1.1,
                  opacity: 0.7
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.3
                }} className="relative bg-dark-bg px-6 py-4 md:px-8 md:py-5 rounded-xl border border-accent/30 shadow-lg shadow-accent/10 min-w-[80px] md:min-w-[100px]" data-id="element-54">
                      <span className="text-4xl md:text-6xl font-bold font-serif text-[#FF9F00] tabular-nums" data-id="element-55">
                        {minutes}
                      </span>
                    </motion.div>
                    <span className="text-xs md:text-sm text-text-secondary/70 mt-2 uppercase tracking-widest font-medium" data-id="element-56">
                      Min
                    </span>
                  </div>

                  {/* Separator */}
                  <motion.span animate={{
                opacity: [1, 0.3, 1]
              }} transition={{
                duration: 1,
                repeat: Infinity
              }} className="text-4xl md:text-6xl font-bold text-accent/60 -mt-6" data-id="element-57">
                    :
                  </motion.span>

                  {/* Seconds */}
                  <div className="flex flex-col items-center" data-id="element-58">
                    <motion.div key={seconds} initial={{
                  scale: 1.1,
                  opacity: 0.7
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.3
                }} className="relative bg-dark-bg px-6 py-4 md:px-8 md:py-5 rounded-xl border border-accent/30 shadow-lg shadow-accent/10 min-w-[80px] md:min-w-[100px]" data-id="element-59">
                      <span className="text-4xl md:text-6xl font-bold font-serif text-[#FF9F00] tabular-nums" data-id="element-60">
                        {seconds}
                      </span>
                    </motion.div>
                    <span className="text-xs md:text-sm text-text-secondary/70 mt-2 uppercase tracking-widest font-medium" data-id="element-61">
                      Seg
                    </span>
                  </div>
                </div>}

              <p className="text-sm text-[#DABFBF]/80 text-center" data-id="element-62">
                Después de que el tiempo llegue a 0, el precio aumentará.
              </p>
            </div>
          </div>
        </motion.div>

        {/* 7. Urgencia extra */}
        <motion.p variants={itemVariants} className="text-base text-accent/90 font-medium mb-5 bg-accent/10 px-6 py-2 rounded-full border border-accent/20" data-id="element-63">
          ⚠️ Si no empiezas ahora, alguien más lo hará antes que tú.
        </motion.p>

        {/* Mensaje motivacional */}
        <motion.p variants={itemVariants} className="text-lg md:text-xl text-text-primary font-medium mb-8 max-w-2xl leading-relaxed text-center" data-id="element-64">
          Estás a un paso de empezar. ✅No necesitas experiencia. ✅No necesitas
          seguidores. Solo necesitas empezar.
        </motion.p>

        {/* 8. Botón Principal */}
        <motion.a variants={itemVariants} href={getCheckoutUrl(addedBumps)} target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.97
      }} className="w-full max-w-md block px-8 py-6 bg-accent text-dark-bg font-black text-xl md:text-2xl rounded-xl shadow-[0_0_40px_rgba(232,168,56,0.35)] hover:bg-[#f0b850] hover:shadow-[0_0_50px_rgba(232,168,56,0.5)] transition-all mb-8" data-id="element-65">
          QUIERO EMPEZAR A GENERAR INGRESOS HOY
        </motion.a>

        {/* 9. Prueba social */}
        <motion.p variants={itemVariants} className="text-sm md:text-base text-text-secondary/80 mb-8 max-w-lg" data-id="element-66">
          🔒 Pago 100% seguro 💸 Garantía de 7 días 📩 Acceso inmediato después
          del pago
        </motion.p>

        {/* Mini Reviews */}
        <motion.div variants={itemVariants} className="w-full max-w-2xl mb-8" data-id="element-67">
          <div className="flex flex-col gap-3" data-id="element-68">
            {[{
            name: 'Carlos M.',
            text: 'En un día ya tenía mi primer video listo. El sistema es claro y fácil de seguir.'
          }, {
            name: 'Andrea L.',
            text: 'No tenía idea de cómo usar IA para crear videos. Ahora tengo un proceso simple que sigo cada vez.'
          }, {
            name: 'Sofía G.',
            text: 'Pasé de no saber qué publicar a tener ideas claras y un proceso para ejecutarlas.'
          }].map((review, i) => <div key={i} className="flex items-start gap-3 bg-dark-secondary/60 border border-white/5 rounded-xl px-5 py-4 text-left" data-id="element-69">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center mt-0.5" data-id="element-70">
                  <span className="text-accent font-semibold text-sm" data-id="element-71">
                    {review.name.charAt(0)}
                  </span>
                </div>
                <div data-id="element-72">
                  <div className="flex items-center gap-2 mb-1" data-id="element-73">
                    <span className="text-text-primary font-semibold text-sm" data-id="element-74">
                      {review.name}
                    </span>
                    <span className="text-accent text-xs" data-id="element-75">★★★★★</span>
                  </div>
                  <p className="text-text-secondary text-sm leading-relaxed" data-id="element-76">
                    "{review.text}"
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* 10. Beneficios */}
        <motion.div variants={itemVariants} className="flex flex-col gap-4 text-left w-full max-w-md bg-dark-secondary/40 p-6 md:p-8 rounded-2xl border border-white/5" data-id="element-77">
          <div className="flex items-start gap-3" data-id="element-78">
            <span className="text-accent font-bold mt-0.5" data-id="element-79">✔</span>
            <span className="text-text-primary" data-id="element-80">Acceso inmediato</span>
          </div>
          <div className="flex items-start gap-3" data-id="element-81">
            <span className="text-accent font-bold mt-0.5" data-id="element-82">✔</span>
            <span className="text-text-primary" data-id="element-83">No necesitas experiencia</span>
          </div>
          <div className="flex items-start gap-3" data-id="element-84">
            <span className="text-accent font-bold mt-0.5" data-id="element-85">✔</span>
            <span className="text-text-primary" data-id="element-86">
              Incluye ideas y guiones listos para usar
            </span>
          </div>
        </motion.div>
      </motion.div>
    </section>;
}